import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { trpc } from '../../lib/trpc';
import { getUser, isAuthenticated } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash, Building2, UserCog } from 'lucide-react';

export function AdminOrganizationsPage() {
  const navigate = useNavigate();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageBuildingsDialogOpen, setIsManageBuildingsDialogOpen] = useState(false);
  const [isManageAdminsDialogOpen, setIsManageAdminsDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
  });
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  const utils = trpc.useUtils();
  const { data: organizations, isLoading: isLoadingOrgs } = trpc.organizations.list.useQuery();
  const { data: buildings, isLoading: isLoadingBuildings } = trpc.buildings.list.useQuery();
  const { data: users, isLoading: isLoadingUsers } = trpc.admin.listResidents.useQuery();
  const { data: orgAdmins } = trpc.admin.listOrganizationAdmins.useQuery();

  const createMutation = trpc.organizations.create.useMutation({
    onSuccess: () => {
      toast.success('Organization created successfully');
      setIsAddDialogOpen(false);
      setFormData({ code: '', name: '' });
      utils.organizations.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.organizations.update.useMutation({
    onSuccess: () => {
      toast.success('Organization updated successfully');
      setIsEditDialogOpen(false);
      setSelectedOrganization(null);
      setFormData({ code: '', name: '' });
      utils.organizations.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.organizations.delete.useMutation({
    onSuccess: () => {
      toast.success('Organization deleted successfully');
      utils.organizations.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const assignBuildingMutation = trpc.organizations.assignBuilding.useMutation({
    onSuccess: () => {
      toast.success('Building assigned successfully');
      setSelectedBuildingId('');
      utils.organizations.list.invalidate();
      utils.buildings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const createOrgAdminMutation = trpc.admin.createOrganizationAdmin.useMutation({
    onSuccess: () => {
      toast.success('Organization admin assigned successfully');
      setIsManageAdminsDialogOpen(false);
      setSelectedUserId('');
      utils.admin.listOrganizationAdmins.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    const user = getUser();
    if (user?.role !== 'system_admin') {
      toast.error('Access denied. System admin only.');
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleEditClick = (org: any) => {
    setSelectedOrganization(org);
    setFormData({
      code: org.code,
      name: org.name,
    });
    setIsEditDialogOpen(true);
  };

  const handleManageBuildingsClick = (org: any) => {
    setSelectedOrganization(org);
    setSelectedBuildingId('');
    setIsManageBuildingsDialogOpen(true);
  };

  const handleManageAdminsClick = (org: any) => {
    setSelectedOrganization(org);
    setSelectedUserId('');
    setIsManageAdminsDialogOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    if (confirm('Are you sure you want to delete this organization?')) {
      deleteMutation.mutate({ id });
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrganization) return;
    updateMutation.mutate({
      id: selectedOrganization.id,
      name: formData.name,
    });
  };

  const handleAssignBuildingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrganization || !selectedBuildingId) return;
    assignBuildingMutation.mutate({
      organizationId: selectedOrganization.id,
      buildingId: selectedBuildingId,
    });
  };

  const handleAssignAdminSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrganization || !selectedUserId) return;
    createOrgAdminMutation.mutate({
      userId: selectedUserId,
      organizationId: selectedOrganization.id,
    });
  };

  const getBuildingCount = (orgId: string) => {
    if (!buildings) return 0;
    return buildings.filter((b: any) => b.organization_id === orgId).length;
  };

  const getOrgBuildings = (orgId: string) => {
    if (!buildings) return [];
    return buildings.filter((b: any) => b.organization_id === orgId);
  };

  const getAvailableBuildings = (orgId: string) => {
    if (!buildings) return [];
    return buildings.filter((b: any) => b.organization_id !== orgId);
  };

  if (isLoadingOrgs || isLoadingBuildings) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <p className="text-sm text-muted-foreground">Administration</p>
          <h1 className="text-2xl font-semibold text-foreground mt-1">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage organizations and their buildings.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Organization</DialogTitle>
                <DialogDescription>
                  Create a new organization.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    placeholder="e.g. ORG1"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Organization Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Organization
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Buildings</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations?.map((org: any) => (
              <TableRow key={org.id} className="hover:bg-muted/50 transition-colors border-b border-border last:border-0">
                <TableCell className="font-medium">{org.code}</TableCell>
                <TableCell>{org.name}</TableCell>
                <TableCell>{getBuildingCount(org.id)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleManageAdminsClick(org)}
                      title="Manage Admins"
                    >
                      <UserCog className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleManageBuildingsClick(org)}
                      title="Manage Buildings"
                    >
                      <Building2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => handleEditClick(org)}
                      title="Edit Organization"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-transparent"
                      onClick={() => handleDeleteClick(org.id)}
                      title="Delete Organization"
                      disabled={getBuildingCount(org.id) > 0}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {organizations?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center h-24">
                  No organizations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update organization details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-code">Code</Label>
              <Input
                id="edit-code"
                value={formData.code}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isManageBuildingsDialogOpen}
        onOpenChange={setIsManageBuildingsDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Buildings</DialogTitle>
            <DialogDescription>
              Assign buildings to {selectedOrganization?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="rounded-md border p-4 bg-muted/50">
              <h3 className="font-medium mb-2">Assigned Buildings</h3>
              {getOrgBuildings(selectedOrganization?.id).length > 0 ? (
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {getOrgBuildings(selectedOrganization?.id).map((b: any) => (
                    <li key={b.id}>{b.name} ({b.code})</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No buildings assigned.</p>
              )}
            </div>

            <form onSubmit={handleAssignBuildingSubmit} className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="building-select">Add Building</Label>
                <Select value={selectedBuildingId} onValueChange={setSelectedBuildingId}>
                  <SelectTrigger id="building-select">
                    <SelectValue placeholder="Select a building to assign..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableBuildings(selectedOrganization?.id).length > 0 ? (
                      getAvailableBuildings(selectedOrganization?.id).map((b: any) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name} ({b.code}) {b.organization_id ? '(Reassign)' : ''}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-buildings" disabled>
                        No available buildings
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Selecting a building already assigned to another organization will move it here.
                </p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={assignBuildingMutation.isPending || !selectedBuildingId}>
                  {assignBuildingMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Assign Building
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isManageAdminsDialogOpen} onOpenChange={setIsManageAdminsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Organization Admins</DialogTitle>
            <DialogDescription>
              Assign users to manage {selectedOrganization?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="rounded-md border p-4 bg-muted/50">
              <h3 className="font-medium mb-2">Current Admins:</h3>
              {orgAdmins && orgAdmins.filter((oa: any) => 
                oa.organization_admins?.some((rel: any) => rel.organization_id === selectedOrganization?.id)
              ).length > 0 ? (
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {orgAdmins.filter((oa: any) => 
                    oa.organization_admins?.some((rel: any) => rel.organization_id === selectedOrganization?.id)
                  ).map((admin: any) => (
                    <li key={admin.id}>{admin.name} ({admin.username})</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No admins assigned.</p>
              )}
            </div>

            <form onSubmit={handleAssignAdminSubmit} className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="user-select">Add User as Admin</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId} required>
                  <SelectTrigger id="user-select">
                    <SelectValue placeholder="Select a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingUsers ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : users && users.length > 0 ? (
                      users.map((user: any) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.username})
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-users" disabled>No users found</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createOrgAdminMutation.isPending || !selectedUserId}>
                  {createOrgAdminMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Assign Admin
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
